import { Link } from 'react-router'
import { getAvailability } from '@/catalog/selectors'
import type { Game } from '@/catalog/types'
import { PlayIcon } from '@/components/ui/icons'
import { cn } from '@/lib/cn'
import { AvailabilityBadge } from './AvailabilityBadge'
import { GameCover } from './GameCover'

export function GameCard({ game }: { game: Game }) {
  const availability = getAvailability(game)
  const playable = availability === 'playable'

  return (
    <Link
      to={`/games/${game.id}`}
      className="group block rounded-card focus-visible:outline-offset-4"
      aria-label={game.title}
    >
      <article>
        <div
          className={cn(
            'relative aspect-[16/10] overflow-hidden rounded-card ring-1 ring-line ring-inset',
            'transition-[transform,box-shadow] duration-300 ease-out-soft',
            'group-hover:-translate-y-0.5 group-hover:shadow-[0_18px_40px_-20px_rgb(0_0_0/0.9)] group-hover:ring-line-strong',
          )}
        >
          <GameCover
            game={game}
            className={cn(
              'absolute inset-0 transition-transform duration-500 ease-out-soft group-hover:scale-[1.04]',
              !playable && 'saturate-[0.8]',
            )}
          />
          {!playable && (
            // Dark backing keeps the tinted badge legible on bright cover art.
            <span className="absolute top-2.5 left-2.5 inline-flex rounded-full bg-ink-950/80 backdrop-blur-md">
              <AvailabilityBadge availability={availability} />
            </span>
          )}
          {playable && (
            <span
              aria-hidden="true"
              className={cn(
                'absolute right-3 bottom-3 grid size-11 place-items-center rounded-full bg-volt text-volt-ink',
                'translate-y-2 opacity-0 transition-[opacity,transform] duration-300 ease-out-soft',
                'group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100',
              )}
            >
              <PlayIcon size={18} className="translate-x-px" />
            </span>
          )}
        </div>
        <h2 className="mt-2.5 truncate px-0.5 text-[0.9375rem] leading-tight font-semibold transition-colors group-hover:text-volt">
          {game.title}
        </h2>
      </article>
    </Link>
  )
}
